import { compareMoreThanEqualsWithTolerance } from "../../../../utilities/compare-with-tolerance";
import Sale from "../../../../utilities/data/model/Sale";
import { FSM } from "../../../../utilities/finite-state-machine/finite-state-machine";
import { StateMachineConfig } from "../../../../utilities/finite-state-machine/types";
import {
  SaleState,
  SaleTransitionData,
  saleStateTransitions,
} from "./sale-state";

export class SaleStateMachine {
  readonly config: StateMachineConfig<SaleState, SaleTransitionData>;

  constructor() {
    this.config = this.initConfig();
  }

  canTransition(currentState: SaleState, newState: SaleState): boolean {
    return new FSM(this.config, currentState).canTransitionTo(newState);
  }

  assertCanTransition(currentState: SaleState, newState: SaleState): void {
    if (!this.canTransition(currentState, newState)) {
      throw new Error(`Cannot transition from ${currentState} to ${newState}`);
    }
  }

  async transition(sale: Sale, state: SaleState, dryRun = false) {
    const fsm = new FSM(this.config, sale.saleState);
    await fsm.transitionTo(state, { sale });
    if (!dryRun) {
      await sale.callWriter(() =>
        sale.updateSaleState(fsm.currentState as any)
      );
    }
  }

  private async onTransitionStart(
    fromState: SaleState,
    toState: SaleState,
    data: SaleTransitionData
  ) {
    const noItems = (await data.sale.items.fetchCount()) === 0;
    const noPayments = data.sale.settledPayments.length;

    /**
     * If the sale is being transitioned to a quote state,
     * check if there are any payments
     *
     * Once a sale has been saved as a quote, it cannot be saved as a sale again
     */
    if (
      ["QUOTE_OPEN", "QUOTE_SAVED"].includes(toState) &&
      !["QUOTE_OPEN", "QUOTE_SAVED"].includes(fromState)
    ) {
      if (!data.sale.customer.id) {
        return "message.sale-must-have-customer-to-be-quoted";
      }
    }

    if (toState.includes("CLOSED")) {
      if (noItems) {
        return "message.must-have-items";
      }
    }

    if (toState === "INVOICE_CLOSED") {
      return;
    }

    if (toState === "QUOTE_CLOSED") {
      return;
    }

    if (toState === "LAYBY_CLOSED") {
      const totals = await data.sale.totals_ASYNC();
      const paymentsTotal = data.sale.paymentsTotal;
      let minDepositAmount =
        totals.totalPrice * ((data.sale.depositPercent ?? 0) / 100);
      if (
        compareMoreThanEqualsWithTolerance(paymentsTotal, minDepositAmount) ===
        false
      ) {
        return "message.deposit-amount-too-low";
      }
      return;
    }

    if (toState === "CLOSED" || toState === "PROFORMA_CLOSED") {
      const balancePaidOff = await data.sale.balancePaidOff_ASYNC();
      if (balancePaidOff === false) {
        return "message.balance-not-paid-off";
      }
      if (noPayments < 1) {
        return "message.must-have-payments";
      }
      return;
    }
  }

  private initConfig(): StateMachineConfig<SaleState, SaleTransitionData> {
    return {
      transitions: saleStateTransitions,

      onTransitionStart: async (fromState, toState, data) => {
        return await this.onTransitionStart(fromState, toState, data);
      },
      onError: async (fromState, toState, message) => {
        throw new Error(
          message ||
            `message.cannot-transition-sale-from-[${fromState}]-to-[${toState}]`
        );
      },
    };
  }
}
